import { useCallback } from 'react'

/**
 * useImageErrorHandler - Standardized image error handling hook
 * 
 * Provides consistent error handling for images across the app:
 * - Hides broken image element
 * - Shows fallback icon container (if available)
 * - Applies parent container styling
 * 
 * Pattern follows AIM principles from claude.md
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onError - Optional custom error handler
 * @param {boolean} options.hideOnError - Hide image on error (default: true)
 * @param {boolean} options.showFallback - Show fallback element (default: true)
 * @returns {Function} Error handler function
 * 
 * @example
 * const handleImageError = useImageErrorHandler()
 * <img src={url} onError={handleImageError} />
 * 
 * @example
 * const handleImageError = useImageErrorHandler({
 *   onError: (e) => console.log('Custom handling'),
 *   hideOnError: false
 * })
 */
export function useImageErrorHandler(options = {}) {
  const {
    onError,
    hideOnError = true,
    showFallback = true
  } = options
  
  return useCallback((e) => {
    const target = e.target
    
    // Hide broken image
    if (hideOnError) {
      target.style.display = 'none'
    }
    
    // Show fallback element (next sibling or parent styling)
    if (showFallback) {
      // Try to show next sibling (fallback icon/component)
      if (target.nextElementSibling) {
        target.nextElementSibling.style.display = 'flex'
      }
      
      // Add fallback class to parent container
      if (target.parentElement) {
        target.parentElement.classList.add('bg-gray-200')
      }
    }
    
    // Call custom error handler if provided
    if (onError) {
      onError(e)
    }
  }, [onError, hideOnError, showFallback])
}

/**
 * Simple inline error handler for basic cases
 * Lightweight alternative when hook is overkill
 */
export const hideImageOnError = (e) => {
  e.target.style.display = 'none'
}
